import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api, type ApiError, type OpenPortalResult } from '@whinerp/client';
import { PortalField, PortalShell, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';
import { portalFieldLabel, portalShellLabels } from './portal-labels.js';
import { CustomerQuote } from './customer-quote.js';
import { PortalHelp } from './portal-help.js';
import { defineScreen } from '../types.js';

/**
 * SC-46 Customer portal.
 *
 * The same shell as the preference centre and the booking page, with different
 * content. What it shows is whatever the server's allowlist returned, and it
 * looks nothing up: a portal that fetched a quote itself could fetch the
 * margin on it, and the filter would be in the browser where a `curl` goes
 * around it.
 *
 * So this screen renders `field.key` and `field.value` without knowing what
 * fields exist. A key it has no word for shows as the key rather than as a
 * blank - a customer seeing `valid_until` knows something is wrong, and a
 * customer seeing nothing does not.
 */

type Portal = OpenPortalResult;

function CustomerPortalScreen(): JSX.Element {
  const t = useT();
  const params = useParams();
  const token = params['token'] ?? '';
  const [open, setOpen] = useState<string | null>(null);

  const page = useQuery<Portal, ApiError>({
    queryKey: ['portal', 'portal', token],
    queryFn: () => api.openPortal({ path: { surface: 'portal' }, query: { token } }),
  });

  if (page.isPending) return <PortalLoading />;

  /*
   * A failed request is a refusal page, not an empty content pane.
   *
   * Without this the shell rendered its content region with no data in it, and
   * a test asserting the region exists passed while the API was answering 500.
   * A customer would have seen a branded page with nothing on it and no way to
   * tell whether that meant "you have nothing" or "this is broken".
   */
  if (page.isError || !page.data) return <PortalError />;

  const data = page.data;

  return (
    <PortalShell
      surface="portal"
      workspaceName={data?.branding?.workspaceName ?? null}
      refusal={data?.refusal ?? null}
      labels={portalShellLabels(t, data?.branding?.workspaceName ?? null)}
    >
      <section className="flex flex-col gap-2">
        <h2 className="text-lead text-ink">{t('portal.yourDetails')}</h2>
        {(data?.contact ?? []).map((field) => (
          <PortalField
            key={field.key}
            data-portal-field={field.key}
            label={portalFieldLabel(t, field.key)}
            value={field.value}
          />
        ))}
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-lead text-ink">{t('portal.quotes')}</h2>
        {(data?.quotes ?? []).length === 0 ? (
          <p data-no-quotes className="text-body text-ink-meta">
            {t('portal.noQuotes')}
          </p>
        ) : (
          <ul data-portal-quotes className="flex flex-col gap-3">
            {(data?.quotes ?? []).map((quote) => (
              <li
                key={quote.quoteId}
                data-portal-quote={quote.quoteId}
                className="flex flex-col gap-3 rounded-card border border-rule p-3"
              >
                <div className="flex flex-wrap items-end gap-4">
                  {quote.fields.map((field) => (
                    <PortalField
                      key={field.key}
                      data-quote-field={field.key}
                      label={portalFieldLabel(t, field.key)}
                      value={field.value}
                    />
                  ))}
                  <button
                    type="button"
                    data-open-quote={quote.quoteId}
                    aria-expanded={open === quote.quoteId}
                    className="ms-auto text-dense text-bronze-text hover:text-ink"
                    onClick={() => setOpen((current) => (current === quote.quoteId ? null : quote.quoteId))}
                  >
                    {open === quote.quoteId ? t('portal.quote.close') : t('portal.quote.open')}
                  </button>
                </div>
                {open === quote.quoteId ? <CustomerQuote token={token} quoteId={quote.quoteId} /> : null}
              </li>
            ))}
          </ul>
        )}
      </section>
      {/* Asking for help, into the same case queue as every other channel (G-193). */}
      <PortalHelp token={token} />
    </PortalShell>
  );
}

function PortalEmpty(): JSX.Element {
  const t = useT();
  return <p className="text-body text-ink-meta">{t('portal.noQuotes')}</p>;
}

function PortalLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex min-h-screen w-full flex-col items-center bg-canvas px-gutter py-6" role="status" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <div className="flex w-full max-w-reading flex-col gap-3">
        <Skeleton className="h-line-h2 w-panel-wide" />
        <Skeleton className="h-plot w-full" />
      </div>
    </div>
  );
}

function PortalError(): JSX.Element {
  const t = useT();
  // The shell's refusal page rather than internal ErrorState chrome.
  return (
    <PortalShell
      surface="portal"
      workspaceName={null}
      refusal={{ kind: 'invalid', canRequestNewLink: false }}
      labels={portalShellLabels(t, null)}
    >
      <span />
    </PortalShell>
  );
}

export const screen = defineScreen({
  id: 'SC-46',
  route: '/portal/:token',
  surface: 'page',
  chrome: 'bare',
  title: 'Your account',
  features: ['F-165', 'F-048', 'F-159'],
  entitlement: 'always-on',
  reachedBy: [{ kind: 'public' }],
  components: ['PortalShell'],
  elements: ['PortalShell', 'PortalField'],
  // A real link, so every check audits the page a customer actually
  // opens rather than its refusal state.
  samplePortalToken: { surface: 'portal', param: 'token' },
  states: { empty: PortalEmpty, loading: PortalLoading, error: PortalError },
  component: CustomerPortalScreen,
});

export default screen;
